import { Injectable, inject } from '@angular/core';

import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { first, flatten, isEqual, orderBy } from 'lodash-es';
import { AppActions, EditTemplateModalId } from 'projects/semeris/src/store/app/app.actions';
import { DatapointFieldActions } from 'projects/semeris/src/store/datapoint-field/datapoint-field.actions';
import { DatapointFieldListActions } from 'projects/semeris/src/store/datapoint-field-list/datapoint-field-list.actions';
import { ExtractionRuleActions } from 'projects/semeris/src/store/extraction-rule/extraction-rule.actions';
import { FormulaActions } from 'projects/semeris/src/store/formula/formula.actions';
import { FormulaListActions } from 'projects/semeris/src/store/formula-list/formula-list.actions';
import { TopicActions } from 'projects/semeris/src/store/topic/topic.actions';
import { TopicListActions } from 'projects/semeris/src/store/topic-list/topic-list.actions';
import { TransactionSelectors } from 'projects/semeris/src/store/transaction/transaction.selectors';
import { UserProfileSelectors } from 'projects/semeris/src/store/user-profile/user-profile.selectors';
import { forkJoin, merge, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { ChecklistService } from '../../checklist/services/checklist.service';
import { Extraction } from '../../extraction/models/extraction.model';
import { ExtractionService } from '../../extraction/services/extraction.service';
import { FormulaService } from '../../formula/services/formula.service';
import {
  EditTemplateModalConfig,
  TemplateModalType,
} from '../../market-comparison/components/edit-template-modal/template-modal.models';
import { ColumnType } from '../../market-comparison/models/column-type.enum';
import { DefinedTermMeta } from '../../market-comparison/models/defined-term-meta.model';
import { Topic } from '../../market-comparison/models/topic.model';
import { TopicService } from '../../market-comparison/services/topic.service';
import { SemerisSelectOption } from '../../shared/components/ng-select/ng-select.component';
import { SemerisRoutes } from '../../shared/constants/routes';
import { UserTemplateEntityBase } from '../../shared/models/common/user-template-entity-base.model';
import { UserTemplateEntityService } from '../../shared/services/user-template-entity.service';
import { DateHelper } from '../../shared/utils/date-helper';
import { DatapointField } from '../../unified-datapoint/models/datapoint-field.models';
import {
  DatapointRule,
  DatapointRuleType,
  RuleBase,
  ruleTypeDisplayNameMapper,
} from '../../unified-datapoint/models/datapoint-rule.model';
import { DatapointHistory } from '../../unified-datapoint/models/datapoint.model';
import { CalculationModeOptions } from '../../unified-datapoint/models/formula-rule.model';
import { DatapointFieldService } from '../../unified-datapoint/services/datapoint-field.service';
import { DatapointRuleService } from '../../unified-datapoint/services/datapoint-rule.service';
import { DatapointService } from '../../unified-datapoint/services/datapoint.service';
import { DependencyGraph, DependencyGraphType } from '../models/dependency-graph.model';
import { DependencyTreeItem, DependencyType } from '../models/dependency-tree-item.model';
import { DependencyGraphService } from '../services/dependency-graph.service';
import { DependencyService } from '../services/dependency.service';
export interface DependencyTreeState {
  id?: string;
  type?: DependencyType;
  loading: boolean;

  dependencyTreeItems?: DependencyTreeItem[];
  viewModels?: TreeItemViewModel[];
  dependencyGraphs?: DependencyGraph[];

  datapointHistoryItems?: DatapointHistory[];

  libraryTransactionId?: string;
  dependencyGraphId?: string;

  extractions?: Extraction[];
  selectedExtraction?: string;
}

export enum LayoutType {
  Empty = 'empty',
  Corner = 'corner',
  Cross = 'cross',
  Vertical = 'vertical',
}

export interface TreeItemViewModel {
  id: string;
  parent?: TreeItemViewModel;
  type: DependencyType;
  displayName: string;
  routerLink?: string[];
  extraInfo?: string;
  entity?: UserTemplateEntityBase;
  level?: number;
  layout?: LayoutType[];
  hideEdit?: boolean;
  active?: 'active' | 'inactive';
  hasLoop?: boolean;
  hash?: string;
  hasValue?: boolean;
  hasError?: boolean;
  lastChangedAt?: string;
}

interface LoadDatapointHistoryParameters {
  datapointHashes: string[];
  libraryTransactionId: string;
}

interface LoadExtractionsParameters {
  datapointHashes: string[];
  libraryTransactionId: string;
  dependencyGraphId: string;
}

type LoadParameters = Pick<DependencyTreeState, 'id' | 'type'>;

@Injectable()
export class DependencyTreeStore extends ComponentStore<DependencyTreeState> {
  private readonly service = inject(DependencyService);
  private readonly checklistService = inject(ChecklistService);
  private readonly topicService = inject(TopicService);
  private readonly datapointRuleService = inject(DatapointRuleService);
  private readonly datapointFieldService = inject(DatapointFieldService);
  private readonly dependencyGraphService = inject(DependencyGraphService);
  private readonly formulaService = inject(FormulaService);
  private readonly datapointService = inject(DatapointService);
  private readonly extractionService = inject(ExtractionService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public loading$ = this.select((state) => state.loading);
  private viewModelsRaw$ = this.select((state) => state.viewModels);
  public datapointHistoryItems$ = this.select((state) => state.datapointHistoryItems);

  public viewModels$ = this.select(
    this.viewModelsRaw$,
    this.datapointHistoryItems$,
    (viewModels, datapointHistoryItems) => {
      const historyItemMap = new Map<string, DatapointHistory>(
        datapointHistoryItems?.map((item) => [item.datapointHash, item])
      );
      return viewModels?.map((vm) => {
        const datapointHistoryItem = historyItemMap.get(vm.hash);
        return {
          ...vm,
          hasValue: datapointHistoryItem?.hasValue,
          hasError: datapointHistoryItem?.hasError,
          lastChangedAt: datapointHistoryItem?.lastChangedAt,
        };
      });
    }
  );

  public libraryTransactionId$ = this.select((state) => state.libraryTransactionId);
  public dependencyGraphId$ = this.select((state) => state.dependencyGraphId);
  public selectedExtraction$ = this.select((state) => state.selectedExtraction);
  private readonly extractionsRaw$ = this.select((state) => state.extractions);
  private readonly dependencyGraphsRaw$ = this.select((state) => state.dependencyGraphs);

  public dependencyGraphs$ = this.select(
    this.dependencyGraphsRaw$,
    this.store.select(UserProfileSelectors.dateTimeFormat),
    (dependencyGraphs, dateTimeFormat) =>
      orderBy(dependencyGraphs, (d) => d.createdAt, 'desc')?.map(
        (option): SemerisSelectOption => ({
          key: option._id,
          value: `${DateHelper.format(option.createdAt, dateTimeFormat)} - ${
            option.type === DependencyGraphType.FullSync ? 'Full Sync' : 'Single Run'
          }`,
          additionalInfo: option.createdBy,
        })
      )
  );
  public transactions$ = this.store.select(TransactionSelectors.list);

  private readonly datapointRules$ = this.select(this.viewModels$, (viewModels) =>
    viewModels?.filter((vm) => vm.type === DependencyType.DatapointRule).map((vm) => vm.entity as DatapointRule)
  );

  private readonly datapointHashes$ = this.select(this.datapointRules$, (datapointRules) =>
    datapointRules?.map((dr) => dr?.ruleHash)
  );

  private readonly loadDatapointHistoryTrigger$ = this.select(
    this.libraryTransactionId$,
    this.datapointHashes$,
    (libraryTransactionId, datapointHashes) => ({
      libraryTransactionId,
      datapointHashes,
    })
  );

  private readonly loadExtractionsTrigger$ = this.select(
    this.libraryTransactionId$,
    this.dependencyGraphId$,
    this.datapointHashes$,
    (libraryTransactionId, dependencyGraphId, datapointHashes) => ({
      libraryTransactionId,
      dependencyGraphId,
      datapointHashes,
    })
  );

  private readonly id$ = this.select((state) => state.id);
  private readonly type$ = this.select((state) => state.type);

  private readonly reloadTrigger$ = new Subject<void>();
  private readonly parameterTrigger$ = this.select(this.id$, this.type$, (id, type) => ({ id, type }), {
    debounce: true,
  });

  private readonly entityTypeModalLookup: Record<DependencyType, EditTemplateModalId> = {
    [DependencyType.Checklist]: EditTemplateModalId.EditChecklist,
    [DependencyType.DatapointField]: EditTemplateModalId.EditDatapointField,
    [DependencyType.DatapointRule]: EditTemplateModalId.EditExtractionRule,
    [DependencyType.Formula]: EditTemplateModalId.EditFormulaList,
    [DependencyType.Topic]: EditTemplateModalId.EditTopicList,
    [DependencyType.SummaryItem]: null,
    [DependencyType.DefinedTerm]: null,
  };

  public extractions$ = this.select(
    this.extractionsRaw$,
    this.datapointRules$,
    this.store.select(UserProfileSelectors.dateTimeFormat),
    (extractions, datapointRules, dateTimeFormat) => {
      const datapointRuleMap = new Map<string, DatapointRule>(datapointRules?.map((dr) => [dr.ruleHash, dr]));

      return orderBy(extractions, (e) => e.updatedAt, 'asc')?.map((e) => {
        const datapointRule = datapointRuleMap.get(e.datapointHash);

        return {
          ...e,
          displayName: datapointRule?.displayName,
          displayDate: e.updatedAt ? DateHelper.format(e.updatedAt, dateTimeFormat) : undefined,
        };
      });
    }
  );

  public noExtractions$ = this.select(
    this.dependencyGraphId$,
    this.libraryTransactionId$,
    this.extractions$,
    (dependencyGraphId, libraryTransactionId, extractions) => {
      return !!dependencyGraphId && !!libraryTransactionId && !extractions?.length;
    }
  );

  constructor() {
    super({ loading: false });

    this.reloadEffect$(this.actions$);
    this.loadExtractionsEffect$(this.loadExtractionsTrigger$);
    this.loadDatapointHistoryEffect$(this.loadDatapointHistoryTrigger$);
    this.selectedExtractionEffect$(this.selectedExtraction$);

    this.loadEffect$(
      merge(this.reloadTrigger$, this.parameterTrigger$.pipe(distinctUntilChanged((a, b) => isEqual(a, b)))).pipe(
        map(
          (): LoadParameters => ({
            id: this.get().id,
            type: this.get().type,
          })
        )
      )
    );
  }

  public ngrxOnStateInit() {
    this.dependencyGraphs(this.dependencyGraphService.getDependencyGraphs());
  }

  private readonly selectedExtractionEffect$ = this.effect((input$: Observable<string>) =>
    input$.pipe(
      distinctUntilChanged((a, b) => isEqual(a, b)),
      filter((selectedExtraction) => !!selectedExtraction),
      debounceTime(200),
      map(() => {
        const items = document.getElementsByClassName('datapoint-value-changed');
        first(items)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      })
    )
  );

  private readonly loadDatapointHistoryEffect$ = this.effect((input$: Observable<LoadDatapointHistoryParameters>) =>
    input$.pipe(
      distinctUntilChanged((a, b) => isEqual(a, b)),
      filter((parameters) => !!parameters?.datapointHashes?.length && !!parameters?.libraryTransactionId),
      switchMap((parameters) =>
        this.datapointService.getDatapointHistory(parameters.datapointHashes, parameters.libraryTransactionId)
      ),
      map((datapointHistoryItems) => {
        this.datapointHistoryItems(datapointHistoryItems);
      })
    )
  );

  private readonly loadExtractionsEffect$ = this.effect((input$: Observable<LoadExtractionsParameters>) =>
    input$.pipe(
      distinctUntilChanged((a, b) => isEqual(a, b)),
      filter(
        (parameters) =>
          !!parameters?.datapointHashes?.length && !!parameters?.libraryTransactionId && !!parameters?.dependencyGraphId
      ),
      switchMap((parameters) =>
        this.extractionService.getExtractions(
          parameters.libraryTransactionId,
          parameters.dependencyGraphId,
          parameters.datapointHashes
        )
      ),
      map((extractions) => {
        this.extractions(extractions);
      })
    )
  );

  private readonly reloadEffect$ = this.effect((input$: Observable<Action>) =>
    input$.pipe(
      ofType(
        DatapointFieldActions.UpdateDatapointFieldSuccess,
        ExtractionRuleActions.UpdateRuleBaseSuccess,
        FormulaActions.UpdateFormulaSuccess,
        FormulaListActions.SaveFormulaDetailsSuccess,
        TopicActions.SaveTopicSuccess,
        TopicListActions.SaveTopicDetailsSuccess,
        DatapointFieldListActions.SetDatapointFieldActivitySuccess
      ),
      map(() => {
        this.reload();
      })
    )
  );

  public reload() {
    this.reloadTrigger$.next(undefined);
  }

  public onEditMetadata(item: TreeItemViewModel) {
    this.store.dispatch(
      AppActions.OpenTemplateModal({
        data: this.itemToTemplateModalConfig(item),
        additionalInfo: { rule: item.entity as RuleBase },
        modalId: this.entityTypeModalLookup[item.type],
      })
    );
  }

  public onChangeActive(item: TreeItemViewModel) {
    if (item.type === DependencyType.DatapointRule) {
      this.store.dispatch(
        ExtractionRuleActions.UpdateRuleBase({
          id: item.id,
          rule: { id: item.id, active: !(item.active === 'active') } as DatapointRule,
        })
      );
    }

    if (item.type === DependencyType.DatapointField) {
      this.store.dispatch(
        DatapointFieldListActions.SetDatapointFieldActivity({
          id: item.id,
          active: !(item.active === 'active'),
        })
      );
    }
  }

  private itemToTemplateModalConfig(item: TreeItemViewModel): EditTemplateModalConfig {
    switch (item.type) {
      case DependencyType.Formula:
        return {
          type: TemplateModalType.Formula,
          data: item.entity,
          myEntity: item.entity?.myEntity,
          title: `Edit ${item.entity?.displayName}`,
          disabled: item.entity?.disabled,
        };
      case DependencyType.Checklist:
        throw new Error('Not implemented');
      case DependencyType.DatapointField:
      case DependencyType.DatapointRule:
        const rule = item.entity as DatapointRule | DatapointField;
        return {
          type: TemplateModalType.RuleBase,
          data: rule,
          myEntity: rule?.myEntity,
          title: `Edit ${rule?.displayName}`,
          presentationType: rule?.presentationType,
          verificationType: rule?.verificationType,
          aiContribution: rule?.aiContribution,
          hasDisplayShortName: true,
          hasIntegrationIdentifier: true,
        };
      case DependencyType.Topic:
        return {
          type: TemplateModalType.Normal,
          data: item.entity,
          myEntity: item.entity?.myEntity,
          title: `Edit ${item.entity?.displayName}`,
          hasIntegrationIdentifier: true,
        };
    }
  }

  private readonly loadEffect$ = this.effect((input$: Observable<LoadParameters>) =>
    input$.pipe(
      filter((parameters) => !!parameters?.id && !!parameters?.type),
      switchMap((parameters) => {
        this.patchState({ loading: true });
        return this.service.getDependencyTreeItems(parameters.id, parameters.type);
      }),
      switchMap((dependencyTreeItems) => {
        this.patchState({ dependencyTreeItems });

        const requests: Observable<TreeItemViewModel[]>[] = [];

        const entities: {
          service: UserTemplateEntityService<UserTemplateEntityBase, any, any>;
          type: DependencyType;
        }[] = [
          { service: this.datapointRuleService, type: DependencyType.DatapointRule },
          { service: this.datapointFieldService, type: DependencyType.DatapointField },
          { service: this.formulaService, type: DependencyType.Formula },
          { service: this.topicService, type: DependencyType.Topic },
          { service: this.checklistService, type: DependencyType.Checklist },
        ];

        for (const entity of entities) {
          const items = dependencyTreeItems.filter((item) => item.type === entity.type);

          if (items.length) {
            requests.push(
              entity.service.getByIds(items.map((item) => item.id)).pipe(
                map((response) =>
                  response.map(
                    (r): TreeItemViewModel => ({
                      id: r.id,
                      displayName: r.nameWithReference,
                      type: entity.type,
                      entity: r,
                      hash: (r as DatapointRule)?.ruleHash,
                    })
                  )
                )
              )
            );
          }
        }

        return forkJoin(requests).pipe(map((results) => flatten(results)));
      }),
      map((viewModels) => {
        const finalList: TreeItemViewModel[] = [];

        const tree = this.get().dependencyTreeItems;

        const treeItemMap = new Map<string, DependencyTreeItem>(tree.map((item) => [item.id, item]));
        const modelMap = new Map<string, TreeItemViewModel>(viewModels.map((vm) => [vm.id, vm]));
        const childrenMap = tree.reduce((acc, tree) => {
          tree.parents.forEach((parent) => {
            if (!acc.has(parent)) {
              acc.set(parent, []);
            }
            acc.get(parent).push(tree.id);
          });

          return acc;
        }, new Map<string, string[]>());

        tree
          .filter((t) => t.type === DependencyType.Topic)
          .forEach((treeItem) => {
            const topicId = treeItem.id;
            childrenMap.set(topicId, []);

            const vm = modelMap.get(topicId);
            const topic = vm.entity as Topic;

            topic.columns?.forEach((column) => {
              switch (column.type) {
                case ColumnType.DefineTerm: {
                  const definedTermMeta = column.value as DefinedTermMeta;
                  const id = uuid();
                  childrenMap.get(topicId).push(id);
                  modelMap.set(id, {
                    id,
                    displayName: definedTermMeta.term,
                    type: DependencyType.DefinedTerm,
                  });
                  break;
                }
                case ColumnType.SummaryTopic: {
                  const id = uuid();
                  childrenMap.get(topicId).push(id);
                  modelMap.set(id, {
                    id,
                    displayName: column.displayName,
                    type: DependencyType.SummaryItem,
                  });
                  break;
                }
                case ColumnType.ParagraphExtractionRule:
                case ColumnType.CalculatedExtractionRule:
                case ColumnType.Field:
                  const datapointId = column.value as string;
                  childrenMap.get(topicId).push(datapointId);
                  break;
                default:
                  break;
              }
            });
          });

        const root = tree.find((item) => !item.parents?.length);

        this.fillChildren(root.id, treeItemMap, childrenMap, modelMap, finalList);

        this.patchState({
          viewModels: this.extendWithLayout(finalList, childrenMap),
          loading: false,
        });
      })
    )
  );

  private extendWithLayout(viewModels: TreeItemViewModel[], childrenMap: Map<string, string[]>): TreeItemViewModel[] {
    const maxLevel = Math.max(...viewModels.map((vm) => vm.level));

    const filledArray = viewModels.map((vm, index) => {
      const layout: LayoutType[] = [];

      for (let i = 1; i <= vm.level; i++) {
        layout.push(LayoutType.Empty);
      }

      return {
        ...vm,
        layout,
      };
    });

    for (let actualLevel = 0; actualLevel < maxLevel; actualLevel++) {
      let currentId: string;
      let hasMoreChildren: boolean;
      let childrenVisited = 0;
      let children: Set<string>;

      filledArray.forEach((item, index) => {
        if (item.level < actualLevel) {
          currentId = undefined;
          return;
        }

        if (item.level === actualLevel) {
          currentId = item.id;
          childrenVisited = 0;
          children = new Set(childrenMap.get(currentId));
          hasMoreChildren = childrenMap.get(currentId)?.length > childrenVisited;
        }

        if (currentId && item.level > actualLevel) {
          if (hasMoreChildren) {
            if (children.has(item.id) && item.level === actualLevel + 1) {
              childrenVisited++;
              hasMoreChildren = childrenMap.get(currentId)?.length > childrenVisited;

              item.layout[actualLevel] = hasMoreChildren ? LayoutType.Cross : LayoutType.Corner;
            } else {
              item.layout[actualLevel] = LayoutType.Vertical;
            }
          }
        }
      });
    }

    return filledArray;
  }

  private fillChildren(
    id: string,
    treeItemMap: Map<string, DependencyTreeItem>,
    childrenMap: Map<string, string[]>,
    modelMap: Map<string, TreeItemViewModel>,
    result: TreeItemViewModel[],
    level = 0,
    allocatedItems: Map<string, Set<string>> = new Map<string, Set<string>>(),
    parent?: TreeItemViewModel
  ): void {
    const treeItem = treeItemMap.get(id);

    const item = modelMap.get(id) || {
      id: treeItem?.id || uuid(),
      type: treeItem?.type,
      hasLoop: treeItem?.hasLoop,
      displayName: 'Access Denied',
      hideEdit: true,
    };

    const extraInfoArray: string[] = [];

    if (item.entity && treeItem?.type === DependencyType.DatapointRule) {
      const datapointRule = item.entity as DatapointRule;
      extraInfoArray.push(ruleTypeDisplayNameMapper[datapointRule.ruleType]);

      if (datapointRule.ruleType === DatapointRuleType.Formula) {
        const calculationMode = datapointRule.rule?.calculationMode;
        const displayText = CalculationModeOptions.find((option) => option.key === calculationMode)?.value;

        if (displayText) {
          extraInfoArray.push(displayText);
        }
      }
    }

    if (treeItem?.type === DependencyType.DatapointField) {
      extraInfoArray.push('Custom Field');
    }

    if (item.entity?.hidden) {
      extraInfoArray.push('hidden');
    }

    if ((item.entity as DatapointField)?.optional) {
      extraInfoArray.push('optional');
    }

    if (treeItem?.overrides?.includes(parent?.id)) {
      extraInfoArray.push('override');
    }

    const node: TreeItemViewModel = {
      ...item,
      displayName: item.displayName || item.entity.nameWithReference,
      hideEdit:
        item.hideEdit ||
        item.type === DependencyType.Checklist ||
        item.type === DependencyType.SummaryItem ||
        item.type === DependencyType.DefinedTerm,
      active:
        item.entity && (item.type === DependencyType.DatapointField || item.type === DependencyType.DatapointRule)
          ? (item?.entity as RuleBase).active
            ? 'active'
            : 'inactive'
          : undefined,
      extraInfo: extraInfoArray.join(', '),
      routerLink: this.getLinkByItem(item),
      level,
      parent: parent,
    };

    result.push(node);

    const children = childrenMap.get(id) || [];

    orderBy(children, (c) => (modelMap.get(c)?.type === DependencyType.Formula ? 1 : 0), 'desc').forEach((childId) => {
      this.fillChildren(childId, treeItemMap, childrenMap, modelMap, result, level + 1, allocatedItems, node);
    });
  }

  public setParams(id: string, type: DependencyType): void {
    this.patchState({ id, type });
  }

  private getLinkByItem(item: TreeItemViewModel): string[] {
    if (!item.entity) {
      return undefined;
    }

    switch (item.type) {
      case DependencyType.Checklist:
        return ['/', SemerisRoutes.checklist.root, item.id];
      case DependencyType.DatapointField:
        return (item.entity as DatapointField).routerLink;
      case DependencyType.DatapointRule:
        return (item.entity as DatapointRule).routerLink;
      case DependencyType.Formula:
        return ['/', SemerisRoutes.formula.root, item.id];
      case DependencyType.Topic:
        return ['/', SemerisRoutes.marketComparison.root, item.id];
      default:
        return undefined;
    }
  }

  public dependencyGraphs = this.updater(
    (state, dependencyGraphs: DependencyGraph[]): DependencyTreeState => ({
      ...state,
      dependencyGraphs,
    })
  );

  public libraryTransactionId = this.updater(
    (state, libraryTransactionId: string): DependencyTreeState => ({
      ...state,
      libraryTransactionId,
      extractions: libraryTransactionId ? state.extractions : null,
    })
  );

  public dependencyGraphId = this.updater(
    (state, dependencyGraphId: string): DependencyTreeState => ({
      ...state,
      dependencyGraphId,
      extractions: dependencyGraphId ? state.extractions : null,
    })
  );

  public datapointHistoryItems = this.updater(
    (state, datapointHistoryItems: DatapointHistory[]): DependencyTreeState => ({
      ...state,
      datapointHistoryItems,
    })
  );

  public extractions = this.updater(
    (state, extractions: Extraction[]): DependencyTreeState => ({
      ...state,
      extractions,
    })
  );

  public selectedExtraction = this.updater(
    (state, selectedExtraction: string): DependencyTreeState => ({
      ...state,
      selectedExtraction,
    })
  );
}
